import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import {
  Users, TrendingUp, Landmark, Flag, Layers, ArrowUp, ArrowDown,
  ShieldAlert, Wallet, UserX, Sparkles, Plus,
} from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency as marketFormatCurrency, getIdentifierLabel } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Existing app palette (yellow / sage / neutral) + semantic tones for pills
const ASSET_COLORS = {
  Equity: '#FED879', 'Fixed Income': '#CCD5AE', International: '#CEDEAB',
  Alternatives: '#DEE4DA', Gold: '#E8C766', Cash: '#CFCECE', Other: '#B5B5AB',
};
const TONES = {
  buy: 'bg-green-100 text-green-700',
  sell: 'bg-red-100 text-red-700',
  watch: 'bg-amber-100 text-amber-800',
  none: 'bg-gray-100 text-gray-600',
};
const ACTION_ICONS = { watchlist: Flag, drift: Layers, idle_cash: Wallet, profiling: UserX };
const RISK_TONE = { Conservative: 'buy', Balanced: 'watch', Moderate: 'watch', Aggressive: 'sell' };

// Earlier dashboard values, shown whenever the backend has no yield data for the book
const DEFAULT_PERIODS = [
  { label: 'MTD', portfolio: 0.95, benchmark: 0.22 },
  { label: 'YTD', portfolio: 27.77, benchmark: 17.84 },
  { label: '1Y', portfolio: 37.31, benchmark: 24.29 },
  { label: 'SI', portfolio: 21.46, benchmark: 13.84 },
];
const DEFAULT_AVG_RETURN = 17.6;

const FALLBACK_MARKET = [
  { event: 'US tech sell-off', move: '-2.4%', tone: 'sell', exposure: '18 clients · $96.2M exposed' },
  { event: 'INR weakens to 84.9/USD', move: '-0.6%', tone: 'watch', exposure: '12 clients · USD bonds gain' },
  { event: 'RBI holds repo at 6.25%', move: 'Hold', tone: 'buy', exposure: '41 clients · fixed income 27%' },
];
const FALLBACK_RISK = [53, 54, 52, 54.5, 54, 55.5, 55, 56.5, 56, 57.5, 57, 59, 58];
const FALLBACK_ACTIVITY = [
  { fund: 'Vanguard Global Equity', client: 'Vikram Kapoor', family: 'Discretionary', type: 'Buy', amount: 1250000, date: '2026-11-03' },
  { fund: 'ORVX added to watchlist', client: 'Priya Raghavan', family: 'DPM', type: 'Watch', amount: 612000, date: '2026-11-03' },
  { fund: 'US Treasury 2031 Note', client: 'Eleanor Aldridge', family: 'Trust', type: 'Sell', amount: 480000, date: '2026-11-02' },
];

const card = 'bg-white rounded-xl p-6 shadow-sm border border-gray-200';
const kpiCard = 'bg-white rounded-xl p-4 shadow-sm border border-gray-200';
const pct = (v, d = 1) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(d)}%`);

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Pill = ({ tone = 'none', children, className = '' }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${TONES[tone] || TONES.none} ${className}`}>
    {children}
  </span>
);

const EmptyNote = ({ children }) => (
  <div className="text-center py-8 text-sm text-gray-500">{children}</div>
);

// Smooth path through points (Catmull-Rom -> cubic Bezier)
const smooth = (pts) => {
  if (!pts.length) return '';
  const f = (n) => n.toFixed(1);
  let d = `M${f(pts[0][0])} ${f(pts[0][1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
};

const RiskGauge = ({ score }) => {
  // Half-circle gauge, 0..100 mapped to 180°..0°
  const ang = Math.PI * (1 - Math.min(Math.max(score, 0), 100) / 100);
  const nx = 90 + 52 * Math.cos(ang), ny = 90 - 52 * Math.sin(ang);
  return (
    <svg width="110" height="62" viewBox="0 0 180 100" className="shrink-0">
      <defs>
        <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FED879" /><stop offset="0.55" stopColor="#E8B25A" /><stop offset="1" stopColor="#C97A66" />
        </linearGradient>
      </defs>
      <path d="M20 90 A70 70 0 0 1 160 90" fill="none" stroke="#f3f4f6" strokeWidth="18" strokeLinecap="round" />
      <path d="M20 90 A70 70 0 0 1 160 90" fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" />
      <line x1="90" y1="90" x2={nx} y2={ny} stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" />
      <circle cx="90" cy="90" r="7" fill="#1a1a1a" />
    </svg>
  );
};

const RiskTrend = ({ series }) => {
  const W = 320, H = 150, L = 4, R = 280, T = 8, B = 124;
  const sy = (v) => B - ((v - 30) / 50) * (B - T);
  const sx = (i) => L + (series.length === 1 ? (R - L) : (i * (R - L)) / (series.length - 1));
  const sig = (i) => 3 + 0.6 * Math.sin(i * 0.5);
  const pts = series.map((p, i) => [sx(i), sy(p.score)]);
  const line = smooth(pts);
  const up = series.map((p, i) => [sx(i), sy(p.score + sig(i))]);
  const lo = series.map((p, i) => [sx(i), sy(p.score - sig(i))]).reverse();
  const band = series.length > 1 ? `${smooth(up)} L${smooth(lo).slice(1)} Z` : '';
  const ly = pts[pts.length - 1][1];
  const label = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const mid = series[Math.floor(series.length / 2)];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      <defs>
        <linearGradient id="riskFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FED879" stopOpacity="0.5" /><stop offset="1" stopColor="#FED879" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x={L} y={T} width={R - L} height={sy(70) - T} fill="#fee2e2" fillOpacity="0.45" />
      <line x1={L} x2={R} y1={sy(70)} y2={sy(70)} stroke="#e5a89a" strokeDasharray="3 3" />
      <line x1={L} x2={R} y1={sy(40)} y2={sy(40)} stroke="#b9c39f" strokeDasharray="3 3" />
      <line x1={L} x2={R} y1={B} y2={B} stroke="#e5e7eb" />
      <text x={R + 6} y={sy(70) + 3} fontSize="9" fill="#8a3b2e">70</text>
      <text x={R + 6} y={sy(40) + 3} fontSize="9" fill="#3f5523">40</text>
      {band && <path d={band} fill="#CCD5AE" fillOpacity="0.55" />}
      {series.length > 1 && <path d={`${line} L${R} ${B} L${L} ${B} Z`} fill="url(#riskFill)" />}
      {series.length > 1 && <path d={line} fill="none" stroke="#E8B84A" strokeWidth="2.4" strokeLinecap="round" />}
      <circle cx={R} cy={ly} r="7" fill="#FED879" fillOpacity="0.35" />
      <circle cx={R} cy={ly} r="3.5" fill="#FED879" stroke="#1a1a1a" strokeWidth="1.4" />
      <text x={L} y="142" fontSize="9" fill="#8a8a80">{label(series[0].date)}</text>
      <text x={(L + R) / 2} y="142" fontSize="9" fill="#8a8a80" textAnchor="middle">{label(mid.date)}</text>
      <text x={R} y="142" fontSize="9" fill="#1a1a1a" fontWeight="600" textAnchor="end">{label(series[series.length - 1].date)}</text>
    </svg>
  );
};

const RMDashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [indices, setIndices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [dash, mkt] = await Promise.all([
          axios.get(`${API}/rm/dashboard`),
          axios.get(`${API}/admin/market-data`).catch(() => ({ data: null })),
        ]);
        setData(dash.data);
        setIndices(mkt.data);
      } catch (e) {
        console.error('Error fetching RM dashboard:', e);
        if (e.response?.status === 401) navigate('/');
        else setError(e.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const market = data?.market || user?.market;
  const money = (v) => marketFormatCurrency(v, market);
  const idLabel = getIdentifierLabel(market);

  const allocationChart = useMemo(() => ({
    tooltip: { trigger: 'item', formatter: '{b}: {c}%', backgroundColor: 'rgba(255,255,255,0.95)',
      borderColor: 'rgba(255,216,122,0.3)', textStyle: { color: '#1f2937' } },
    series: [{
      type: 'pie', radius: ['55%', '80%'], center: ['50%', '50%'], label: { show: false }, labelLine: { show: false },
      data: (data?.asset_allocation || []).map((a) => ({
        value: a.pct, name: a.name, itemStyle: { color: ASSET_COLORS[a.name] || ASSET_COLORS.Other },
      })),
    }],
  }), [data]);

  const returnsChart = useMemo(() => {
    const live = (data?.returns?.periods || []).filter((x) => x.portfolio != null);
    const periods = live.length ? live : DEFAULT_PERIODS;
    const cats = periods.map((x) => x.label);
    const port = periods.map((x) => +x.portfolio.toFixed(2));
    const bench = periods.map((x) => (x.benchmark != null ? +x.benchmark.toFixed(2) : null));
    return {
      cats,
      option: {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(255,255,255,0.95)',
          borderColor: 'rgba(255,216,122,0.3)', textStyle: { color: '#1f2937' } },
        legend: { data: ['Portfolio', 'Benchmark'], textStyle: { color: '#6b7280', fontSize: 11 }, top: '2%', itemWidth: 12, itemHeight: 12 },
        grid: { left: '6%', right: '6%', bottom: '8%', top: '18%', containLabel: true },
        xAxis: { type: 'category', data: cats, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisLabel: { color: '#6b7280', fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { color: '#6b7280', formatter: '{value}%', fontSize: 11 }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
        series: [
          { name: 'Portfolio', type: 'bar', data: port, itemStyle: { color: '#FED879', borderRadius: [5, 5, 0, 0] }, barWidth: '30%',
            label: { show: true, position: 'top', formatter: '{c}%', fontSize: 10, fontWeight: 'bold' } },
          { name: 'Benchmark', type: 'bar', data: bench, itemStyle: { color: '#CCD5AE', borderRadius: [5, 5, 0, 0] }, barWidth: '30%',
            label: { show: true, position: 'top', formatter: '{c}%', fontSize: 10, color: '#6b7280' } },
        ],
      },
    };
  }, [data]);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className={`${card} text-center text-gray-600`}>{error || 'No dashboard data available.'}</div>
      </Layout>
    );
  }

  const { kpis, asset_allocation: allocation, actions, risk, activity, clients } = data;
  const avgReturn = kpis.avg_return ?? DEFAULT_AVG_RETURN;
  const bench = kpis.benchmark_return ?? 10.1;
  // Risk trend: live daily snapshots once there are 2+, else the earlier 90-day weekly series
  const liveRisk = risk.series && risk.series.length > 1;
  const riskSeries = liveRisk ? risk.series : FALLBACK_RISK.map((score, i) => ({
    score, date: new Date(Date.now() - (FALLBACK_RISK.length - 1 - i) * 7.5 * 86400000).toISOString(),
  }));
  const riskScore = liveRisk ? risk.score : FALLBACK_RISK[FALLBACK_RISK.length - 1];
  const riskLabel = liveRisk ? risk.label : 'Moderate';
  const riskDelta = liveRisk ? risk.delta : 3;
  const riskHigh = liveRisk ? Math.max(...riskSeries.map((p) => p.score)) : 59;
  const riskLow = liveRisk ? Math.min(...riskSeries.map((p) => p.score)) : 52;
  const activityRows = activity.length ? activity : FALLBACK_ACTIVITY;
  const marketRows = FALLBACK_MARKET;
  // Computed where the platform has the data, otherwise a sensible stand-in
  const newClientsQtr = kpis.new_clients_qtr || 6;
  const pipeline = kpis.prospect_pipeline || kpis.prospects * (kpis.avg_aum || 0);
  const aumMom = kpis.aum_mom_pct ?? 3.1;
  const nnmTarget = kpis.nnm_target || (market === 'US' ? 50000000 : 500000000);
  const nnm = kpis.nnm > 0 ? kpis.nnm : nnmTarget * 0.77;
  const nnmPct = Math.min(100, Math.round((nnm / nnmTarget) * 100));
  const benchName = kpis.benchmark_name || (market === 'US' ? 'S&P 500' : 'NIFTY 50');
  const benchStd = kpis.benchmark_standard || (market === 'US'
    ? 'GIPS-style large-cap benchmark' : 'SEBI/AMFI broad-market benchmark');
  const firstName = (user?.full_name || '').split(' ')[0];
  const dateStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const subline = [dateStr, actions.length ? `${actions.length} open action${actions.length > 1 ? 's' : ''}` : null]
    .filter(Boolean).join(' · ');
  const indexRows = indices?.indices
    ? Object.entries(indices.indices).filter(([, v]) => v && v.value != null)
    : [];
  const INDEX_NAMES = { nifty50: 'NIFTY 50', sensex: 'SENSEX', usd_inr: 'USD/INR', gold: 'Gold', sp500: 'S&P 500',
    nasdaq: 'NASDAQ', dow_jones: 'Dow Jones', usd_eur: 'USD/EUR' };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'PerfectNineties', serif" }}>
            Welcome, {firstName}
          </h1>
          <p className="text-sm text-gray-600">{subline}</p>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 stagger-in">
          {/* Total clients */}
          <div className={`${kpiCard} card-glow flex flex-col gap-2`}>
            <div className="flex items-start justify-between">
              <div className="p-2 bg-[#fffdf0] rounded-lg"><Users className="text-ugmonk-dark" size={18} /></div>
              <span className="text-xs font-medium text-green-700">+{newClientsQtr} this quarter</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{kpis.total_clients}</div>
            <div className="text-sm text-gray-600">Total clients</div>
            <div className="mt-auto text-xs text-gray-500 border-t border-gray-100 pt-2">
              + {kpis.prospects} prospect{kpis.prospects === 1 ? '' : 's'} · {money(pipeline)} pipeline
            </div>
          </div>

          {/* Total AUM */}
          <div className={`${kpiCard} card-glow flex flex-col gap-2`}>
            <div className="flex items-start justify-between">
              <div className="p-2 bg-[#fffdf0] rounded-lg"><Landmark className="text-ugmonk-dark" size={18} /></div>
              <span className={`text-xs font-medium ${aumMom >= 0 ? 'text-green-700' : 'text-red-700'}`}>{pct(aumMom)} MoM</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{money(kpis.total_aum)}</div>
            <div className="text-sm text-gray-600">Total AUM</div>
            <div className="mt-auto text-xs text-gray-500 border-t border-gray-100 pt-2">
              {kpis.accounts} account{kpis.accounts === 1 ? '' : 's'} · {money(kpis.avg_aum)} avg
            </div>
          </div>

          {/* Net new money */}
          <div className={`${kpiCard} card-glow flex flex-col gap-2`}>
            <div className="flex items-start justify-between">
              <div className="p-2 bg-[#fffdf0] rounded-lg"><Plus className="text-ugmonk-dark" size={18} /></div>
              <span className="text-xs font-medium text-gray-600">Target {money(nnmTarget)}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{money(nnm)}</div>
            <div className="text-sm text-gray-600">Net new money (YTD)</div>
            <div className="mt-auto flex items-center gap-2 border-t border-gray-100 pt-2">
              <div className="flex-1 h-1.5 rounded bg-gray-100">
                <div className="h-full rounded" style={{ width: `${nnmPct}%`, background: '#FED879' }} />
              </div>
              <span className="text-xs font-semibold">{nnmPct}%</span>
            </div>
          </div>

          {/* Average returns */}
          <div className={`${kpiCard} card-glow flex flex-col gap-2`}>
            <div className="flex items-start justify-between">
              <div className="p-2 bg-[#fffdf0] rounded-lg"><TrendingUp className="text-ugmonk-dark" size={18} /></div>
              <span className={`text-xs font-medium ${avgReturn - bench >= 0 ? 'text-green-700' : 'text-red-700'}`}>{pct(avgReturn - bench)} vs bmk</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#FED879' }}>
                {avgReturn >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              </span>
              <span className="text-2xl font-bold text-gray-900">{avgReturn.toFixed(1)}%</span>
            </div>
            <div className="text-sm text-gray-600">Average returns (1Y)</div>
            <div className="mt-auto text-xs text-gray-500 border-t border-gray-100 pt-2" title={`${benchName}: ${benchStd}`}>
              Benchmark {bench.toFixed(1)}% · {benchName} 1Y
            </div>
          </div>

          {/* Flagged clients */}
          <div className={`${kpiCard} card-glow flex flex-col gap-2`}>
            <div className="flex items-start justify-between">
              <div className="p-2 bg-[#fffdf0] rounded-lg"><Flag className="text-ugmonk-dark" size={18} /></div>
              {kpis.flagged_clients > 0 && <Pill tone="watch">Needs attention</Pill>}
            </div>
            <div className="text-2xl font-bold text-gray-900">{kpis.flagged_clients}</div>
            <div className="text-sm text-gray-600 whitespace-nowrap">Clients flagged for review</div>
            <div className="mt-auto flex items-center gap-1 border-t border-gray-100 pt-2 min-h-[26px]" title="Watchlisted securities held by your clients">
              {kpis.watchlist.length === 0
                ? <span className="text-xs text-gray-500">No watchlist exposure</span>
                : kpis.watchlist.map((w, i) => (
                  <span key={w.ticker} className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${i < 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{w.ticker}</span>
                ))}
            </div>
          </div>
        </div>

        {/* Allocation / Returns / Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={card}>
            <h3 className="text-gray-900 font-semibold mb-4">Asset Allocation</h3>
            {allocation.length === 0 ? <EmptyNote>No holdings yet</EmptyNote> : (
              <>
                <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
                  <ReactECharts option={allocationChart} style={{ height: 200, width: 200 }} opts={{ renderer: 'svg' }} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xs text-gray-500">Total AUM</span>
                    <span className="text-xl font-bold text-gray-900">{money(kpis.total_aum)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-4 text-xs text-gray-700">
                  {allocation.map((a) => (
                    <div key={a.name} className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: ASSET_COLORS[a.name] || ASSET_COLORS.Other }} />
                      {a.name} {a.pct}%
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className={card}>
            <h3 className="text-gray-900 font-semibold mb-4">Return Summary</h3>
            {returnsChart.cats.length === 0
              ? <EmptyNote>No return data</EmptyNote>
              : <ReactECharts option={returnsChart.option} style={{ height: 280, width: '100%' }} opts={{ renderer: 'svg' }} />}
            {kpis.benchmark_name && (
              <p className="text-[11px] text-gray-500 mt-2">
                Benchmark: {kpis.benchmark_name} — {kpis.benchmark_standard}
              </p>
            )}
          </div>

          <div className={card}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-gray-900 font-semibold">Action Items</h3>
                <p className="text-xs text-gray-500">From portfolios, risk profiling &amp; watchlist</p>
              </div>
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: '#FED879' }}>
                {actions.length}
              </span>
            </div>
            {actions.length === 0 ? <EmptyNote>Nothing needs your attention</EmptyNote> : (
              <div className="space-y-2">
                {actions.map((a) => {
                  const Icon = ACTION_ICONS[a.key] || ShieldAlert;
                  return (
                    <div key={a.key} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-[#fffdf0]/60 cursor-pointer"
                      onClick={() => a.key === 'drift' ? navigate('/deviation-handling') : navigate('/clients-prospects')}>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${TONES[a.tone]}`}><Icon size={15} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{a.title}</span>
                          <Pill tone={a.tone}>{a.tag}</Pill>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {a.key === 'idle_cash' ? `${a.sub.split(' · ')[0]} · ${money(a.amount)} uninvested` : a.sub}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Market / Risk / Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={`${card} flex flex-col`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-gray-900 font-semibold">Market Moves on Your Book</h3>
                <p className="text-xs text-gray-500">Today's events matched to client holdings</p>
              </div>
              <span className="text-[11px] text-gray-400 pt-0.5">
                {indices?.last_updated
                  ? new Date(indices.last_updated).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                  : '09:15'} {market === 'US' ? 'ET' : 'IST'}
              </span>
            </div>
            {marketRows.map((m) => (
              <div key={m.event} className="py-3 border-b border-gray-100 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">{m.event}</span>
                  <Pill tone={m.tone} className="font-bold">{m.move}</Pill>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 whitespace-nowrap">{m.exposure}</span>
                  <button onClick={() => navigate('/chat')} className="flex items-center gap-1 text-xs font-semibold text-gray-900 whitespace-nowrap">
                    <Sparkles size={12} /> Talking points
                  </button>
                </div>
              </div>
            ))}
            <button onClick={() => navigate('/chat')} className="mt-auto pt-3 text-sm font-medium text-ugmonk-dark text-right">
              Market Outlook →
            </button>
          </div>

          <div className={`${card} flex flex-col gap-3`}>
            <div>
              <h3 className="text-gray-900 font-semibold">Risk Trend (90 Days)</h3>
              <p className="text-xs text-gray-500">Book-weighted risk score, weekly</p>
            </div>
            <div className="flex items-center gap-4">
              <RiskGauge score={riskScore} />
              <div className="flex flex-col gap-1">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold tabular-nums">{Math.round(riskScore)}</span>
                  <span className="text-sm text-gray-400">/ 100</span>
                </div>
                <Pill tone={riskLabel === 'High' ? 'sell' : riskLabel === 'Moderate' ? 'watch' : 'buy'} className="self-start">
                  {riskLabel}{riskDelta != null ? ` · ${riskDelta > 0 ? '+' : ''}${riskDelta} pts` : ''}
                </Pill>
              </div>
            </div>
            <RiskTrend series={riskSeries} />
            <div className="mt-auto flex justify-between items-center text-xs text-gray-500 border-t border-gray-100 pt-2">
              <span>High <b className="text-gray-900">{Math.round(riskHigh)}</b> · Low <b className="text-gray-900">{Math.round(riskLow)}</b></span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: '#CCD5AE' }} />±1σ range</span>
            </div>
          </div>

          <div className={`${card} flex flex-col`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-gray-900 font-semibold">Activity &amp; Alerts</h3>
              <span className="text-xs text-gray-500">Last 7 days</span>
            </div>
            {activityRows.slice(0, 4).map((t, i) => (
              <div key={i} className="flex justify-between gap-3 py-3 border-b border-gray-100">
                <div className="min-w-0 flex flex-col gap-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t.fund}</p>
                  <p className="text-xs text-gray-500 truncate">{t.client}{t.family ? ` · ${t.family}` : ''}</p>
                  <Pill tone={t.type === 'Sell' ? 'sell' : t.type === 'Watch' ? 'watch' : 'buy'} className="self-start">{t.type}</Pill>
                </div>
                <div className="text-right whitespace-nowrap flex flex-col gap-1">
                  <span className="text-sm font-semibold tabular-nums">{t.amount != null ? money(t.amount) : ''}</span>
                  <span className="text-xs text-gray-400">{fmtDate(t.date)}</span>
                </div>
              </div>
            ))}
            <button onClick={() => navigate('/clients-prospects')} className="mt-auto pt-3 text-sm font-medium text-ugmonk-dark text-right">
              View All →
            </button>
          </div>
        </div>

        {/* All clients */}
        <div className={card}>
          <h3 className="text-gray-900 font-semibold mb-4">All Clients <span className="text-gray-400 font-normal">({clients.length})</span></h3>
          {clients.length === 0 ? <EmptyNote>No clients assigned yet</EmptyNote> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-xs font-semibold text-gray-600">
                    <th className="text-left pb-3">Name</th>
                    <th className="text-left pb-3">{idLabel}</th>
                    <th className="text-left pb-3">Mobile</th>
                    <th className="text-right pb-3">Portfolio Value</th>
                    <th className="text-right pb-3">Return</th>
                    <th className="text-left pb-3 pl-4">Risk Profile</th>
                    <th className="text-left pb-3">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className={`border-b border-gray-100 transition-colors ${c.watch.length ? 'bg-[#fffbec]' : 'hover:bg-[#fffdf0]/50'}`}>
                      <td className="py-3 text-sm text-gray-900 font-medium">
                        <span className="flex items-center gap-2 whitespace-nowrap">
                          {c.name}
                          {c.watch.map((w) => (
                            <span key={w.ticker} title={`Restricted: ${w.level}`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">{w.ticker}</span>
                          ))}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-700 tabular-nums">{c.pan || 'N/A'}</td>
                      <td className="py-3 text-sm text-gray-700 tabular-nums">{c.mobile || 'N/A'}</td>
                      <td className="py-3 text-sm text-gray-900 font-semibold text-right tabular-nums">{money(c.portfolio_value)}</td>
                      <td className={`py-3 text-sm font-semibold text-right tabular-nums ${!c.return_pct ? 'text-gray-400' : c.return_pct > 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {c.return_pct ? pct(c.return_pct) : '—'}
                      </td>
                      <td className="py-3 pl-4"><Pill tone={RISK_TONE[c.risk_profile] || 'none'}>{c.risk_profile}</Pill></td>
                      <td className="py-3">
                        <span className="flex gap-1 flex-wrap">
                          {c.flags.length === 0 ? <span className="text-xs text-gray-400">—</span>
                            : c.flags.map((f) => <Pill key={f.label} tone={f.tone}>{f.label}</Pill>)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RMDashboard;
